import { assign, createMachine, sendParent } from 'xstate';
import { EcommMachineEvents } from './types/ecommMachine.types';
import { NavigationSections } from '../constants';
import {
  EntryPointEvents,
  EntryPointMachineContext,
  EntryPointMachineEvents,
  EntryPointMachineStates,
  EntryPointStates,
} from './types/entrypointMachine.types';
import authorizeCard from '../networkRequests/debitCard/authorizeCard';
import getApplicationId from '../utils/commonState/getApplicationId';
import { PropagateNetworkErrors } from './actions/genericActions';
import { identify, page, track } from './actions/segment';
import selectLeaseOption from '../networkRequests/lease/selectLeaseOption';
import { MakeStateCallingQueryOrder, MakeStateCallingQueryOrderWithCartValidation } from './refresh-order';
import { ChannelTypes, FormTypes } from '../gql/graphql';
import { LongFormStates } from './types/longFormMachine.types';
import { OtherCharges } from '../networkRequests/cart/updateOrderItems';

export default createMachine<EntryPointMachineContext, EntryPointMachineEvents, EntryPointMachineStates>(
  {
    id: 'EntryPoint',
    initial: EntryPointStates.Initial,
    context: {} as EntryPointMachineContext,
    on: {
      [EntryPointEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [EntryPointEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [EntryPointEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
      },
    },
    states: {
      [EntryPointStates.Initial]: {
        tags: 'Loading',
        entry: ['log'],
        always: [
          { target: EntryPointStates.OTPRequest, cond: 'OTPRequest' },
          { target: EntryPointStates.PreApproval, cond: 'PreApproval' },
          { target: EntryPointStates.ContractSummaryLoading, cond: 'ContractSummary' },
          { target: EntryPointStates.BankAccountError, cond: 'BankAccountError' },
          { target: EntryPointStates.DebitCardError, cond: 'DebitCardError' },
          { target: EntryPointStates.LeaseDeclined, cond: 'LeaseDeclined' },
          { target: EntryPointStates.LoanDeclined, cond: 'LoanDeclined' },
          { target: EntryPointStates.ApprovalConfirmation, cond: 'ApprovalConfirmation' },
          { target: EntryPointStates.LeaseSigned, cond: 'Done' },
        ],
      },
      [EntryPointStates.OTPRequest]: {
        id: 'OTPRequest',
        initial: 'waiting',
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
        states: {
          waiting: {
            on: {
              [EntryPointEvents.SEND_TOKEN]: {
                type: 'final',
                actions: [
                  sendParent((_context, event) => ({
                    type: EcommMachineEvents.PHONE_SUBMITTED,
                    data: { cellPhone: event.data.cellPhone },
                  })),
                ],
              },
              [EntryPointEvents.SKIP_OTP]: {
                type: 'final',
                actions: [
                  assign({ cellPhone: (_, event) => event.data.cellPhone, didSkipOTP: true }),
                  sendParent((context) => ({
                    type: EcommMachineEvents.SKIP_OTP_EVENT,
                    data: { ...context },
                  })),
                ],
              },
            },
          },
        },
      },
      [EntryPointStates.PreApproval]: {
        initial: 'callingQueryOrder',
        states: {
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
          waiting: { on: { [EcommMachineEvents.NEXT]: { target: 'callingAuthorizeCard', actions: assign({ authorizeCardErrors: [] }) } } },
          callingAuthorizeCard: {
            tags: 'callingAuthorizeCard',
            invoke: {
              src: (context: EntryPointMachineContext, event: EntryPointMachineEvents) => authorizeCard(getApplicationId(context.application)),
              onDone: [
                {
                  cond: (_context, event) => !!event.data.hardDeclineError,
                  target: '#EntryPoint.Done',
                  actions: [
                    assign({ authorizeCardErrors: (context, event) => event.data.authorizeCardErrors }),
                    sendParent((context) => ({
                      type: EcommMachineEvents.LONG_FORM,
                      data: {
                        ...context,
                        source: 'Declined',
                      },
                    })),
                    track('Debit Card Error', { errType: 'HardDecline' }),
                  ],
                },
                {
                  cond: (_context, event) => !!event.data.softDeclineError,
                  target: '#EntryPoint.Done',
                  actions: [
                    assign({
                      application: (_, event) => event?.data?.application,
                      authorizeCardErrors: (context, event) => event.data.authorizeCardErrors,
                    }),
                    sendParent((context) => ({
                      type: EcommMachineEvents.LONG_FORM,
                      data: { ...context, source: 'Error' },
                    })),
                    (context, event) => {
                      track('Debit Card Error', { errType: 'SoftDecline', decisionCode: event.data.softDeclineError?.extensions?.decisionCode })(
                        context,
                        event,
                      );
                    },
                  ],
                },
                {
                  cond: (_context, event) => !!event.data.authorizeCardErrors?.length,
                  target: 'waiting', // let the page handle it
                  actions: [assign({ authorizeCardErrors: (_context, event) => event.data.authorizeCardErrors })],
                },
                {
                  type: 'final',
                  cond: 'isEcommLongForm',
                  actions: sendParent((_context) => ({
                    type: EcommMachineEvents.LONG_FORM,
                    data: { source: 'ContractSummary' },
                  })),
                },
                // I feel like this will cause a bug because transitionFrom will not be sent
                {
                  target: '#EntryPoint.Done',
                  actions: sendParent((context) => ({
                    type: EcommMachineEvents.LONG_FORM,
                    data: { ...context, source: 'Approved' },
                  })),
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
        on: {
          [EntryPointEvents.APPROVED]: [
            {
              type: 'final',
              cond: 'isEcommLongForm',
              actions: sendParent((_context) => ({
                type: EcommMachineEvents.LONG_FORM,
                data: { source: 'ContractSummary' },
              })),
            },
            {
              type: 'final',
              actions: sendParent((_context) => ({
                type: EcommMachineEvents.LONG_FORM,
                data: { source: 'Approved' },
              })),
            },
          ],
          [EntryPointEvents.DECLINED_OR_ERROR]: {
            type: 'final',
            actions: sendParent((_context, event) => ({
              type: EcommMachineEvents.LONG_FORM,
              data: { source: !!event.data.hardDeclineError ? 'Declined' : 'Error' },
            })),
          },
          [EntryPointEvents.COMPLETE_APP_LATER]: {
            target: EntryPointStates.Done,
            actions: sendParent((context) => ({
              type: EcommMachineEvents.LONG_FORM,
              data: { ...context, source: 'CompleteAppLater' },
            })),
          },
        },
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
          canGoBackViaHeader: false,
        },
      },

      [EntryPointStates.ContractSummaryLoading]: {
        tags: 'Loading',
        initial: 'callingQueryOrder',
        states: {
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('continueOrRouteBackToItems'),
          continueOrRouteBackToItems: {
            always: [
              {
                cond: (context) => !!context.application?.lease?.cartValidationAdditionalDetails && context.dealer.isVirtualCard,
                target: '#EntryPoint.Done',
                actions: sendParent((context) => ({
                  type: EcommMachineEvents.EXISTING_APPLICATION,
                  data: { ...context },
                })),
              },
              { target: '#EntryPoint.ContractSummary' },
            ],
          },
        },
      },
      [EntryPointStates.ContractSummary]: {
        initial: 'waiting',
        states: {
          waiting: {
            on: {
              [EntryPointEvents.NEXT]: {
                target: 'callingSelectLeaseOption',
                actions: assign({ selectLeaseOptionErrors: [] }),
              },
            },
          },
          callingSelectLeaseOption: {
            tags: 'calling',
            invoke: {
              src: selectLeaseOption,
              onDone: [
                {
                  cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                  target: 'waiting',
                  actions: assign({ selectLeaseOptionErrors: (_context, event) => event.data.selectLeaseOptionErrors }),
                },
                {
                  cond: (ctx) => ctx.dealer.formType === FormTypes.Long || ctx.dealer.formType === FormTypes.LongV2,
                  type: 'final',
                  actions: sendParent((context) => ({
                    type: EcommMachineEvents.LONG_FORM,
                    data: { ...context, source: LongFormStates.LeaseAgreement }, // use this pattern when passing between machines
                  })),
                },
                {
                  type: 'final',
                  actions: sendParent((context) => ({
                    type: EcommMachineEvents.EXISTING_APPLICATION,
                    data: { ...context, source: 'ContractSummary.NEXT' },
                  })),
                },
              ],
              onError: { actions: PropagateNetworkErrors() },
            },
          },
        },
        on: {
          [EntryPointEvents.BACK]: [
            {
              type: 'final',
              // refresh handler to update the cart with the order items
              cond: (context) => !context?.cart?.length,
              actions: [
                assign({
                  cart: (context) =>
                    context.orderDetails?.items
                      ?.filter((item) => item.displayName !== OtherCharges) // ignore fees
                      .map((item, id) => ({
                        itemName: item.displayName,
                        quantity: item.quantity,
                        price: (Math.round(parseFloat(item.price) * 100) * item.quantity) / 100,
                        id: id, // this is the iterator which will serve as a unique ascending id
                      })) || [],
                }),
                sendParent((context) => ({ type: EcommMachineEvents.EXISTING_APPLICATION, data: { ...context, source: 'ContractSummary.BACK' } })),
              ],
            },
            {
              type: 'final',
              actions: sendParent((context) => ({ type: EcommMachineEvents.EXISTING_APPLICATION, data: { ...context, source: 'ContractSummary.BACK' } })),
            },
          ],
          [EntryPointEvents.EDIT_DEBIT_INFO]: {
            target: EntryPointStates.Done,
            actions: sendParent((context) => ({
              type: EcommMachineEvents.EXISTING_APPLICATION,
              data: { ...context, source: 'ContractSummary.EDIT_DEBIT_INFO' },
            })),
          },
        },
        meta: {
          navigationSection: NavigationSections.REVIEW_AND_PAY,
        },
      },
      [EntryPointStates.BankAccountError]: {
        id: 'BankAccountError',
        on: {
          [EntryPointEvents.NEXT]: {
            target: EntryPointStates.Done,
            actions: sendParent((context) => ({
              type: EcommMachineEvents.EXISTING_APPLICATION,
              data: { ...context, source: 'BankAccountError.NEXT' },
            })),
          },
        },
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
      },
      [EntryPointStates.DebitCardError]: {
        id: 'DebitCardError',
        on: {
          [EntryPointEvents.NEXT]: {
            target: EntryPointStates.Done,
            actions: sendParent((context) => ({
              type: EcommMachineEvents.EXISTING_APPLICATION,
              data: { ...context, source: 'DebitCardError.NEXT' },
            })),
          },
        },
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
      },
      [EntryPointStates.ApprovalConfirmation]: {
        entry: (context) => {
          if (context.highestSupportedApplicationMilestone > 3)
            console.log('entry context:', {
              role: context.role,
              applicationItems: context.applicationItems,
              queryParams: document.location.search,
              milestone: context.highestSupportedApplicationMilestone,
              appStatus: context.application?.lease?.status,
            });
        },
      },
      [EntryPointStates.LoanDeclined]: {},
      [EntryPointStates.LeaseDeclined]: {},
      [EntryPointStates.LeaseSigned]: {},
      [EntryPointStates.Done]: { type: 'final' },
      // OR-2278: removed DraftAppReviewAndSubmit here; its only behavior was
      // firing transferToRapid() on entry, which no longer exists.
    },
    schema: {
      context: {} as EntryPointMachineContext,
      events: {} as EntryPointMachineEvents,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      // strictly for debugging, do not remove and make sure you clean up references in MRs
      log: (context, event) => {
        console.log('Entrypoint');
        console.log('context', context);
        console.log('event', event);
      },
    },
    guards: {
      ApprovalConfirmation: (context: EntryPointMachineContext) => context.page === 'ApprovalConfirmation',
      PreApproval: (context: EntryPointMachineContext) => context.page === 'PreApproval',
      OTPRequest: (context: EntryPointMachineContext) => context.page === 'OTPRequest',
      ContractSummary: (context: EntryPointMachineContext) => context.page === 'ContractSummary',
      BankAccountError: (context: EntryPointMachineContext) => context.page === 'BankAccountError',
      LeaseDeclined: (context: EntryPointMachineContext) => context.page === 'LeaseDecline',
      LoanDeclined: (context: EntryPointMachineContext) => context.page === 'LoadDecline',
      DebitCardError: (context: EntryPointMachineContext) => context.page === 'DebitCardError',
      Done: (context: EntryPointMachineContext) => context.page === 'Done',
      isEcommLongForm: (context, _event) => {
        return (
          (context?.dealer?.formType === FormTypes.Long || context?.dealer?.formType === FormTypes.LongV2) &&
          context.dealer.channelType === ChannelTypes.Ecommerce
        );
      },
      isEcommLongFormApproval: (context, _event) =>
        (context.dealer?.formType === FormTypes.Long || context.dealer?.formType === FormTypes.LongV2) && context.application?.lease?.status === 'approved',
    },
  },
);
